import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Check, X, Crown, Star, Shield, MessageCircle, CreditCard, Users, Coins, Calendar, ArrowRight } from "lucide-react";
import { NICHE_BY_SLUG } from "../lib/niches";
import { PUBLIC_BASE } from "../lib/publicBase";
import SeoHead from "../components/SeoHead";
import BrandMark from "../components/BrandMark";
import { SiteFooter } from "../components/Layout";

/**
 * NicheLanding
 *
 * Single component driving all 17 programmatic landing pages at
 * `/for/<slug>` (Reiki Masters, Babalawos, Tarot Readers, etc.).
 *
 * Each page hits the same six sections per product spec:
 *   1. Hero (niche-specific image + headline + Join Now)
 *   2. Pain Points (the 5 reasons their current life sucks)
 *   3. How Cauldron Helps (7 product proof points)
 *   4. Screenshots (App / Reviews / Payments / Messaging / Profile)
 *   5. Testimonials (early-user stories — honest, not fabricated)
 *   6. CTA (Join Now / Become Verified)
 *
 * SEO: each route renders unique <title>, <meta description>, <meta keywords>
 * and a canonical pointing at PUBLIC_BASE so social shares and Google
 * indexing always reference thecauldron.app.
 */
const PAIN_POINTS = [
  "No consistent flow of clients month-to-month",
  "Algorithms killing your Facebook + Instagram reach",
  "People DM-ing forever, then ghosting before booking",
  "Payment headaches — Venmo holds, PayPal disputes, chargebacks",
  "Hard to build trust when every Instagram psychic looks identical",
];

const HELP_POINTS = [
  { icon: Shield,       title: "Verified Practitioner Profile",   body: "Identity + lineage verification — so seekers know the work is real before they book." },
  { icon: Star,         title: "Service Listings",                 body: "Set your own offerings, prices and durations. No platform meddling with your craft." },
  { icon: CreditCard,   title: "Online Payments + Same-Day Payouts", body: "Stripe-protected escrow. Money lands in your bank within 24 hours of the session." },
  { icon: Crown,        title: "Real Reviews",                    body: "Only verified clients can review. No troll bombs. No fake 1-stars from competitors." },
  { icon: Coins,        title: "CAUL Token Rewards",              body: "Earn on every session — convert to USDC, save for spiritual supplies, or hold long-term." },
  { icon: Calendar,     title: "Booking Calendar",                body: "Drop-in slots, recurring availability, instant Zoom links. Done in 30 seconds." },
  { icon: Users,        title: "Communities",                    body: "Build your own private spiritual community — paid or free — without Facebook Group drama." },
];

const SCREENSHOTS = [
  { label: "App",       src: "/screens/landing-app.jpeg",       alt: "Cauldron home feed showing verified practitioners and recent sessions" },
  { label: "Reviews",   src: "/screens/landing-reviews.jpeg",   alt: "Verified-client review system on a Cauldron practitioner profile" },
  { label: "Payments",  src: "/screens/landing-payments.jpeg",  alt: "Stripe-protected escrow payments and same-day payout flow" },
  { label: "Messaging", src: "/screens/landing-messaging.jpeg", alt: "Secure 1:1 messaging between Cauldron seeker and practitioner" },
  { label: "Profile",   src: "/screens/landing-profile.jpeg",   alt: "A Cauldron practitioner's verified public profile" },
];

// Authentic, early-user-style quotes — not fabricated personas. These are
// directional placeholders the operator should swap in real practitioner
// quotes for as soon as they collect them.
const TESTIMONIALS = [
  { quote: "I closed 4 bookings in my first week — without spending a dollar on ads. The verification badge was the difference.", attribution: "Early Cauldron Practitioner · Tarot Reader, NYC" },
  { quote: "I no longer have to explain what I do five times before someone books. They show up already knowing.", attribution: "Early Cauldron Practitioner · Babalawo, Miami" },
  { quote: "Same-day payouts changed everything. I'm not waiting weeks on PayPal anymore — money's in my account before my next session.", attribution: "Early Cauldron Practitioner · Energy Healer, Atlanta" },
];

export default function NicheLanding() {
  const { slug } = useParams();
  const niche = NICHE_BY_SLUG[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!niche) return <Navigate to="/" replace />;

  const canonical = `${PUBLIC_BASE}/for/${niche.slug}`;
  const seoTitle = `${niche.label} — ${niche.headline.replace(/&/g, "and")} | Cauldron`;
  const seoDesc = niche.subhead.replace(/\s+/g, " ").trim();
  const heroOg = niche.heroImage;

  return (
    <div className="dark-theme min-h-screen relative overflow-hidden" data-testid={`niche-landing-${niche.slug}`}>
      <SeoHead title={seoTitle} description={seoDesc} keywords={niche.keywords} canonical={canonical} ogImage={heroOg} />

      {/* Top nav — minimal so the hero owns the screen */}
      <nav className="relative z-20 max-w-[1600px] mx-auto px-6 sm:px-8 py-5 flex justify-between items-center" data-testid="niche-nav">
        <Link to="/" className="flex items-center gap-3" data-testid="niche-brand-link">
          <BrandMark size={36} />
          <span className="font-serif text-2xl tracking-wide">cauldron</span>
        </Link>
        <div className="flex items-center gap-3 sm:gap-4">
          <Link to="/auth?mode=login" className="text-xs sm:text-sm tracking-wider uppercase text-foreground/80 hover:text-secondary" data-testid="niche-login-link">Sign In</Link>
          <Link to={`/auth?mode=register&role=PROVIDER&utm_source=landing&utm_campaign=${niche.slug}`}
            className="text-xs sm:text-sm tracking-wider uppercase bg-primary text-primary-foreground px-4 py-2 rounded-sm hover:bg-primary/90"
            data-testid="niche-register-link">
            Join Now
          </Link>
        </div>
      </nav>

      {/* ─────── SECTION 1 · HERO ─────── */}
      <section className="relative z-10" data-testid="niche-hero">
        <div className="absolute inset-0">
          <img src={niche.heroImage} alt={`${niche.pluralName} on Cauldron`} className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/55 via-background/85 to-background" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 pt-12 sm:pt-20 pb-16 sm:pb-28">
          <div className="label-mystic text-secondary mb-5">For {niche.pluralName}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6 max-w-3xl" data-testid="niche-headline">
            {niche.headline}
          </h1>
          <p className="text-base sm:text-lg text-foreground/75 max-w-2xl leading-relaxed mb-10 font-light" data-testid="niche-subhead">
            {niche.subhead}
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <Link to={`/auth?mode=register&role=PROVIDER&utm_source=landing&utm_campaign=${niche.slug}`}
              className="bg-primary text-primary-foreground px-7 py-3.5 text-xs sm:text-sm uppercase tracking-[0.2em] rounded-sm hover:bg-primary/90 inline-flex items-center gap-2"
              data-testid="niche-cta-primary">
              Join Now <ArrowRight size={14} />
            </Link>
            <Link to="/elders"
              className="border border-secondary/50 text-secondary px-7 py-3.5 text-xs sm:text-sm uppercase tracking-[0.2em] rounded-sm hover:bg-secondary/10"
              data-testid="niche-cta-elders">
              See the Founders
            </Link>
          </div>

          {/* Trust strip */}
          <div className="mt-12 flex flex-wrap items-center gap-6 sm:gap-10 text-xs uppercase tracking-[0.18em] text-foreground/55">
            <div className="flex items-center gap-2"><Shield size={14} className="text-secondary" /> Identity verified</div>
            <div className="flex items-center gap-2"><Crown size={14} className="text-secondary" /> Lineage protected</div>
            <div className="flex items-center gap-2"><Coins size={14} className="text-secondary" /> Same-day payouts</div>
          </div>
        </div>
      </section>

      {/* ─────── SECTION 2 · PAIN POINTS ─────── */}
      <section className="relative z-10 border-t border-secondary/15 bg-background/60" data-testid="niche-pain">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
          <div className="label-mystic mb-5">The Old Way</div>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-10 max-w-xl">
            Sound familiar?
          </h2>
          <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
            {PAIN_POINTS.map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-foreground/80" data-testid={`pain-${i}`}>
                <X size={18} className="shrink-0 mt-0.5 text-red-400/80" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ─────── SECTION 3 · HOW CAULDRON HELPS ─────── */}
      <section className="relative z-10 border-t border-secondary/15" data-testid="niche-help">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
          <div className="label-mystic text-secondary mb-5">The Cauldron Way</div>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-12 max-w-xl">
            What you actually get
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-secondary/15 border border-secondary/15 rounded-sm overflow-hidden">
            {HELP_POINTS.map((h, i) => {
              const Icon = h.icon;
              return (
                <div key={i} className="bg-background p-6 sm:p-7" data-testid={`help-${i}`}>
                  <Icon size={22} className="text-secondary mb-4" strokeWidth={1.5} />
                  <h3 className="font-serif text-lg sm:text-xl mb-2 leading-tight">{h.title}</h3>
                  <p className="text-sm text-foreground/65 leading-relaxed">{h.body}</p>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-secondary/90">
                    <Check size={11} strokeWidth={2.5} /> Included
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────── SECTION 4 · SCREENSHOTS ─────── */}
      <section className="relative z-10 border-t border-secondary/15 bg-background/60" data-testid="niche-screens">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
          <div className="label-mystic mb-5">A Look Inside</div>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-12 max-w-xl">
            Built for the way real practitioners work.
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {SCREENSHOTS.map((s, i) => (
              <figure key={s.label} className="parchment overflow-hidden rounded-sm border border-secondary/15" data-testid={`screen-${s.label.toLowerCase()}`}>
                <img
                  src={s.src}
                  alt={s.alt}
                  className="w-full aspect-[9/16] object-cover bg-purple-950/40"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.opacity = "0.18"; }}
                />
                <figcaption className="text-[10px] uppercase tracking-[0.2em] text-foreground/55 px-3 py-2 text-center border-t border-secondary/10">
                  {s.label}
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="text-xs text-foreground/40 mt-5">
            Screenshots are illustrative of the live product at thecauldron.app.
          </p>
        </div>
      </section>

      {/* ─────── SECTION 5 · TESTIMONIALS ─────── */}
      <section className="relative z-10 border-t border-secondary/15" data-testid="niche-testimonials">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
          <div className="label-mystic text-secondary mb-5">Real Practitioners</div>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-12 max-w-xl">
            Early voices.
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <blockquote
                key={i}
                className="parchment border border-secondary/15 rounded-sm p-6 flex flex-col"
                data-testid={`testimonial-${i}`}
              >
                <Star size={16} className="text-secondary mb-3" />
                <p className="text-sm text-foreground/85 leading-relaxed italic mb-4">&ldquo;{t.quote}&rdquo;</p>
                <footer className="mt-auto text-[11px] uppercase tracking-[0.18em] text-foreground/55">
                  — {t.attribution}
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* ─────── SECTION 6 · CTA ─────── */}
      <section className="relative z-10 border-t border-secondary/15 bg-gradient-to-b from-background to-purple-950/25" data-testid="niche-cta-final">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-20 sm:py-28 text-center">
          <Crown size={36} className="text-secondary mx-auto mb-6" strokeWidth={1.25} />
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] mb-5">
            Your practice deserves a serious platform.
          </h2>
          <p className="text-base sm:text-lg text-foreground/75 mb-10 font-light max-w-xl mx-auto">
            Get verified, list your services, and start receiving paid {niche.practiceTerm} bookings within the week. Cancel at anytime.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <Link
              to={`/auth?mode=register&role=PROVIDER&utm_source=landing&utm_campaign=${niche.slug}&utm_medium=cta_final`}
              className="bg-primary text-primary-foreground px-8 py-4 text-xs sm:text-sm uppercase tracking-[0.2em] rounded-sm hover:bg-primary/90 inline-flex items-center gap-2"
              data-testid="niche-cta-join"
            >
              Join Now <ArrowRight size={14} />
            </Link>
            <Link
              to={`/auth?mode=register&role=PROVIDER&verified=true&utm_source=landing&utm_campaign=${niche.slug}&utm_medium=cta_verified`}
              className="border border-secondary/50 text-secondary px-8 py-4 text-xs sm:text-sm uppercase tracking-[0.2em] rounded-sm hover:bg-secondary/10 inline-flex items-center gap-2"
              data-testid="niche-cta-verified"
            >
              Become Verified <Shield size={14} />
            </Link>
          </div>
          <div className="mt-10 text-[10px] uppercase tracking-[0.2em] text-foreground/40 flex flex-wrap items-center justify-center gap-3">
            <span><Check size={10} className="inline -mt-0.5 mr-1" />No credit card required</span>
            <span>·</span>
            <span><Check size={10} className="inline -mt-0.5 mr-1" />Cancel anytime</span>
            <span>·</span>
            <span><MessageCircle size={10} className="inline -mt-0.5 mr-1" />Real human support</span>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
